import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { mdiEye, mdiEyeOff, mdiInformationOutline } from '@mdi/js';
import { useEffect, useState } from 'react';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { T } from '@transifex/react';

interface Props extends FormControlProps {
  error?: string | undefined;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  labelTooltip?: string;
  name: string;
  value?: string;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  dataTestId?: string;
  onFocus?: () => void;
  isDisabled?: boolean;
  pt?: number;
  maxW?: string;
  helperText?: React.ReactNode;
  placeholder?: string;
  isTextarea?: boolean;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
  showPasswordToggle?: boolean;
  resetVisibilityKey?: string | number | boolean;
}

export const InputFormControl: React.FC<Props> = ({
  error,
  isInvalid,
  isRequired = false,
  isReadOnly = false,
  label,
  labelTooltip,
  name,
  value = '',
  onChange,
  onBlur,
  dataTestId = `input-element-${name}`,
  onFocus,
  isDisabled,
  pt = 0,
  maxW = 'md',
  helperText,
  placeholder = '',
  isTextarea = false,
  type = 'text',
  autoComplete,
  maxLength,
  showPasswordToggle = false,
  resetVisibilityKey,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(false);
  }, [resetVisibilityKey]);

  const resolvedType =
    showPasswordToggle && type === 'password'
      ? isVisible
        ? 'text'
        : 'password'
      : type;

  return (
    <FormControl {...{ isInvalid, isRequired, pt, maxW }}>
      {labelTooltip ? (
        <HStack spacing={1} mb={2} align='center'>
          <FormLabel mb={0} mr={0}>
            <T _str={label} />
          </FormLabel>
          <Tooltip label={labelTooltip} placement='top' hasArrow maxW='xs'>
            <IconButton
              aria-label={`More info about ${label}`}
              icon={
                <PortalIcon path={mdiInformationOutline} w='16px' h='16px' />
              }
              size='xs'
              variant='ghost'
              minW='auto'
              h='auto'
              p={0}
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              _focus={{ outline: 'none' }}
              _focusVisible={{
                outline: '2px solid var(--colors-primary-500)',
                outlineOffset: '2px',
              }}
            />
          </Tooltip>
        </HStack>
      ) : (
        <FormLabel>
          <T _str={label} />
        </FormLabel>
      )}
      {isTextarea ? (
        <Textarea
          {...{
            name,
            value,
            onChange,
            onBlur,
            onFocus,
            isDisabled,
            isReadOnly,
            placeholder,
          }}
        />
      ) : showPasswordToggle && type === 'password' ? (
        <InputGroup>
          <Input
            {...{
              name,
              value,
              onChange,
              onBlur,
              onFocus,
              isDisabled,
              isReadOnly,
              placeholder,
              autoComplete,
              maxLength,
            }}
            type={resolvedType}
            data-testid={dataTestId}
          />
          <InputRightElement>
            <IconButton
              aria-label={isVisible ? 'Value is visible' : 'Value is hidden'}
              icon={<PortalIcon path={isVisible ? mdiEye : mdiEyeOff} />}
              variant='ghost'
              size='sm'
              isDisabled={isDisabled || isReadOnly}
              onClick={() => setIsVisible((v) => !v)}
              data-testid={`${dataTestId}-toggle-visibility`}
            />
          </InputRightElement>
        </InputGroup>
      ) : (
        <Input
          {...{
            name,
            value,
            onChange,
            onBlur,
            onFocus,
            isDisabled,
            isReadOnly,
            placeholder,
            type,
            autoComplete,
            maxLength,
          }}
          data-testid={dataTestId}
        />
      )}
      <FormErrorMessage>{error}</FormErrorMessage>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
